import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Clock, RefreshCcw } from "lucide-react";

function RejectDialog({ tx, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full h-8 text-red-600 border-red-200 hover:bg-red-50"
          data-testid={`reject-tx-${tx.transaction_id}`}
        >
          <XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject submission · {tx.transaction_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>
            Rejecting {tx.customer_name} · {tx.final_points} pts submitted by{" "}
            <span className="font-mono">{tx.submitted_by}</span>.
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why are you rejecting this submission?"
            data-testid={`reject-reason-${tx.transaction_id}`}
          />
        </div>
        <DialogFooter>
          <Button
            disabled={busy || reason.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm(reason.trim());
                setOpen(false);
                setReason("");
              } catch (e) {
                toast.error(formatError(e));
              } finally {
                setBusy(false);
              }
            }}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full"
            data-testid={`reject-confirm-${tx.transaction_id}`}
          >
            Confirm reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ApprovalsPanel({ onBalanceChange }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const p = await api.get("/transactions", { params: { status: "pending" } });
      setPending(p.data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const [confirmed, setConfirmed] = useState({});
  const approve = async (tx) => {
    try {
      await api.post(`/transactions/${tx.transaction_id}/approve`,
                     { confirm_first_purchase: !!confirmed[tx.transaction_id] });
      toast.success(`Approved · ${tx.final_points} pts for ${tx.customer_name}`);
      onBalanceChange?.();
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const reject = async (tx, reason) => {
    await api.post(`/transactions/${tx.transaction_id}/reject`, { reason });
    toast.success("Rejected");
    load();
  };

  return (
    <div className="space-y-8" data-testid="approvals-panel">
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ letterSpacing: "-0.01em" }}>
            Pending staff submissions
            <span className="ml-2 label-caps">{pending.length} waiting</span>
          </h2>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-approvals">
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
        {loading ? (
          <div className="w-card-flat p-6" style={{ color: "var(--text-muted)" }}>Loading…</div>
        ) : pending.length === 0 ? (
          <div className="w-card-flat p-8 text-center" style={{ color: "var(--text-muted)" }}>
            <Clock className="h-5 w-5 mx-auto mb-2 opacity-60" />
            No submissions awaiting approval.
          </div>
        ) : (
          <div className="w-card w-card-bordered overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--bg-soft)" }}>
                  <th className="text-left px-4 py-3 label-caps">Member</th>
                  <th className="text-left px-4 py-3 label-caps">Mode · Service</th>
                  <th className="text-right px-4 py-3 label-caps">Points</th>
                  <th className="text-left px-4 py-3 label-caps">Submitted by</th>
                  <th className="text-left px-4 py-3 label-caps">When</th>
                  <th className="text-right px-4 py-3 label-caps">Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((tx) => (
                  <tr key={tx.transaction_id} className="border-t"
                      style={{ borderColor: "var(--border)" }}
                      data-testid={`pending-row-${tx.transaction_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{tx.customer_name}</div>
                      <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
                        {tx.customer_id}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                      <span className="font-semibold" style={{ color: "var(--text)" }}>
                        {tx.type === "Referral Bonus" ? `Referral · ${tx.referral_side}` : tx.type}
                      </span>{" · "}
                      {tx.mode}{tx.service_type ? ` · ${tx.service_type}` : ""}
                      {tx.invoice_amount ? ` · ${tx.invoice_currency || "AED"} ${tx.invoice_amount}` : ""}
                      {tx.profit_amount != null && <span data-testid={`approval-profit-${tx.transaction_id}`}>{` · Profit ${tx.profit_currency} ${tx.profit_amount.toLocaleString()}`}</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{tx.final_points > 0 ? "+" : ""}{tx.final_points}</td>
                    <td className="px-4 py-3 text-xs font-mono">{tx.submitted_by}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                      {new Date(tx.submitted_at || tx.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                        {tx.type === "Referral Bonus" && (
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <input type="checkbox" checked={!!confirmed[tx.transaction_id]}
                                   onChange={(e) => setConfirmed({ ...confirmed, [tx.transaction_id]: e.target.checked })}
                                   data-testid={`confirm-purchase-${tx.transaction_id}`} />
                            First purchase confirmed
                          </label>
                        )}
                        <Button
                          size="sm"
                          disabled={tx.type === "Referral Bonus" && !confirmed[tx.transaction_id]}
                          onClick={() => approve(tx)}
                          className="rounded-full h-8 text-white"
                          style={{ background: "var(--emerald)" }}
                          data-testid={`approve-tx-${tx.transaction_id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve
                        </Button>
                        <RejectDialog tx={tx} onConfirm={(r) => reject(tx, r)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

    </div>
  );
}
